/**
 * Leadership Burden Diagnostic - Results Page (v7.0.0)
 *
 * Displays zone-specific results (Foundational/Stretch/Mastery) with:
 * - Value analysis breakdown
 * - Intervention channel split (System 68.5% / Leadership 31.5%)
 * - Top 3 leadership anxieties requiring attention
 * - Evidence-based recommendations
 * - Zone theming
 *
 * Uses unified CSS design system (17 modular files)
 * Connected to Algorithm v2.0 calculations
 *
 * Copyright © 2026 Daring Futures. All rights reserved.
 * Proprietary and confidential.
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PERSONA_BASELINES } from '../../utils/algorithms';
import DiagnosticHeader from '../../components/diagnostics/DiagnosticHeader';

// Import unified CSS
import '../../styles/diagnostics/base.css';
import '../../styles/diagnostics/components/pies.css';
import '../../styles/diagnostics/components/legends.css';
import '../../styles/diagnostics/components/interventions.css';
import '../../styles/diagnostics/components/dependencies.css';
import '../../styles/diagnostics/components/recommendations.css';
import '../../styles/diagnostics/components/highlight-box.css';

function LeadershipBurdenResults() {
  const { code } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  const [zone, setZone] = useState('foundational');
  const [frameworkData, setFrameworkData] = useState({ dependencies: [], anxieties: [] });

  // Zone determination function (aligned with algorithms.js thresholds)
  const determineZone = (lpff) => {
    if (lpff < 0.50) return 'foundational';
    if (lpff < 0.85) return 'stretch';
    return 'mastery';
  };

  // Load zone CSS dynamically
  useEffect(() => {
    if (zone) {
      import(`../../styles/diagnostics/zones/${zone}.css`);
    }
  }, [zone]);

  // Fetch results and framework data from API
  useEffect(() => {
    const fetchData = async () => {
      if (!code) {
        setError('No diagnostic code provided');
        setLoading(false);
        return;
      }

      try {
        // Fetch results and framework data in parallel
        const [resultsResponse, frameworkResponse] = await Promise.all([
          fetch(`/api/get-leadership-burden-results?code=${code}`),
          fetch('/api/public-framework-data')
        ]);

        const resultsData = await resultsResponse.json();
        const frameworkDataResponse = await frameworkResponse.json();

        if (resultsData.success && resultsData.results) {
          setResults(resultsData.results);

          // Determine zone based on LPFF
          const determinedZone = determineZone(resultsData.results.calculations.lpffCalculated);
          setZone(determinedZone);
        } else {
          setError(resultsData.message || 'Failed to load results');
        }

        // Set framework data (dependencies and anxieties from database)
        if (frameworkDataResponse.success) {
          setFrameworkData({
            dependencies: frameworkDataResponse.dependencies || [],
            anxieties: frameworkDataResponse.anxieties || []
          });
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load results. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [code]);

  // Get zone-specific content
  const getZoneContent = () => {
    const totalValue = results?.valueAtStake || 15;
    const stretchValueAvailable = totalValue * 0.067; // 6.7% stretch potential

    if (zone === 'foundational') {
      return {
        highlightLabel: 'Below Baseline Threshold',
        highlightValue: 'Foundational',
        highlightSubtext: 'Building essential leadership psychological flexibility to access average transformation value (57%)',
        challengerQuestion1: `"Your leadership flexibility scores place you below the baseline threshold. This means you're not yet positioned to access the 57% average value that most transformations achieve. If we could develop your leadership psychological flexibility to that capability, what would this mean for your £${totalValue}M transformation?"`,
        challengerQuestion2: '"The dark green shows what baseline capability looks like—the 57% average value most transformations achieve. The light blue? That\'s stretch potential once you\'ve built the foundation. First step: build foundational leadership psychological flexibility to reach baseline. Are you ready?"',
        keyInsight: 'Building foundational leadership psychological flexibility unlocks access to transformation value. Your scores indicate you\'re below the baseline threshold needed to access the 57% average value. Once you reach baseline, stretch interventions become effective and accessible value increases significantly.'
      };
    } else if (zone === 'stretch') {
      return {
        highlightLabel: 'Remaining Potential Value',
        highlightValue: `£${stretchValueAvailable.toFixed(2)}M`,
        highlightSubtext: 'With your solid foundation you can stretch further, are you ready?',
        challengerQuestion1: `"If your transformation is worth £${totalValue}M, how much of that will you actually get?"`,
        challengerQuestion2: `"The dark green slice? That's your foundational value. The light green slice? That's your enhanced value gain by working towards excellence. The last £${stretchValueAvailable.toFixed(2)}M is your stretch. Do you want it?"`,
        keyInsight: 'Your baseline capability is strong. Stretch interventions can now unlock additional value beyond the average transformation. Focus on optimizing the highest-impact leadership anxieties to maximize your capture rate within the empirical ceiling.'
      };
    } else {
      return {
        highlightLabel: 'Remaining Potential Value',
        highlightValue: `£${(totalValue * 0.018).toFixed(2)}M`,
        highlightSubtext: 'Although you\'re currently in the top 10% you could stretch further, are you ready?',
        challengerQuestion1: `"If your transformation is worth £${totalValue}M, you're already capturing £${(totalValue * 0.652).toFixed(2)}M — 8 points above average. The question is: can you fine-tune to reach the ceiling?"`,
        challengerQuestion2: '"The dark green? That\'s your foundational value. The light green? That\'s your enhanced value gain as a top performer. Your stretch is the final 1.8 points to reach peak performance. Excellence awaits you."',
        keyInsight: 'You\'re approaching the empirical ceiling for transformation value capture. The priority shifts to maintaining this exceptional capability under pressure. Focus on resilience, sustainability, and preventing regression in your top leadership anxieties.'
      };
    }
  };

  // Get top 3 leadership anxieties using database data
  const getTop3Anxieties = () => {
    if (!results?.scores) return [];

    // Map framework data to anxieties with scores
    const scoredAnxieties = frameworkData.anxieties.map((anx, index) => ({
      id: anx.id,
      name: anx.genericAnxiety,
      summary: anx.top3Summary,
      impact: anx.impactStatement,
      actionSummary: anx.actionSummary || '',
      actionStatement: anx.actionStatement || '',
      score: results.scores[`anx${index + 1}Score`] || 5
    }));

    // Sort by score (highest first) and take top 3
    return scoredAnxieties.sort((a, b) => b.score - a.score).slice(0, 3);
  };

  // Get score band class
  const getScoreBand = (score) => {
    if (score >= 8) return 'critical';
    if (score >= 6) return 'weak';
    if (score >= 4) return 'moderate';
    if (score >= 2) return 'strong';
    return 'excellent';
  };

  // Impact text now comes from database (anx.impact field)

  // Loading state
  if (loading) {
    return (
      <div className="container" style={{ textAlign: 'center', padding: '80px 16px' }}>
        <div style={{ fontSize: '18px', color: '#697A90' }}>Loading your results...</div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="container" style={{ textAlign: 'center', padding: '80px 16px' }}>
        <div style={{ fontSize: '18px', color: '#dc2626', marginBottom: '24px' }}>{error}</div>
        <button onClick={() => navigate('/diagnostics/leadership-burden')} className="btn-primary">
          Start New Diagnostic
        </button>
      </div>
    );
  }

  // Main content
  const persona = PERSONA_BASELINES[results.personaId] || PERSONA_BASELINES.mark;

  // Calculate value breakdown
  const totalValue = results.valueAtStake || 15;
  const foundationalValue = totalValue * 0.57; // 57% baseline
  const potentialStretch = totalValue * 0.10; // 10% stretch potential
  const permanentlyLost = totalValue * 0.33; // 33% friction

  // Stretch zone specific calculations (4 segments)
  const stretchValueGained = totalValue * 0.033; // 3.3% extra value gained
  const stretchValueAvailable = totalValue * 0.067; // 6.7% stretch potential

  // Mastery zone specific calculations (4 segments)
  const masteryValueGained = totalValue * 0.082; // 8.2% extra value gained
  const masteryValueAvailable = totalValue * 0.018; // 1.8% stretch potential remaining

  const totalPotential = foundationalValue + potentialStretch;
  const systemValue = totalPotential * 0.685; // 68.5% system
  const leadershipValue = totalPotential * 0.315; // 31.5% leadership

  const zoneContent = getZoneContent();
  const top3Anxieties = getTop3Anxieties();

  return (
    <>
      <DiagnosticHeader />
      <div className={`zone-${zone}`}>
        <div className="container">
        {/* Header */}
        <div className="card">
          <div className="badge">STEP 4 OF 4</div>
          <h1>Your Leadership Burden Assessment Results</h1>
          <p style={{ color: '#697A90', marginBottom: '16px' }}>
            Leadership psychological flexibility analysis and transformation value recovery potential, for <strong>{results.leaderName || 'Leader'}</strong>
          </p>
          <div className="persona-info">
            <p><strong>Selected Profile:</strong> {persona.name} • {persona.role}</p>
            <p><strong>Leadership Strain:</strong> {persona.strain}</p>
          </div>
        </div>

        {/* Value Analysis */}
        <div className="card">
          <h2>Value Analysis</h2>

          {/* Challenger Question */}
          <div className="challenger-question">
            {zoneContent.challengerQuestion1}
          </div>

          <p style={{ marginBottom: '8px' }}>
            McKinsey studied thousands of transformations. Here's what's happening to your business case and benefits realisation plan:
          </p>

          <div className="pie-section">
            <div className="pie-container">
              <div className="pie-chart pie-chart-full"></div>
              <div className="pie-center">
                <div className="pie-center-label">Total Value</div>
                <div className="pie-center-value">£{totalValue}M</div>
              </div>
            </div>

            <div className="legend">
              {zone === 'stretch' ? (
                <>
                  <div className="legend-item">
                    <div className="legend-color color-captured"></div>
                    <div className="legend-content">
                      <div className="legend-title">Foundational value realised</div>
                      <div className="legend-description">Your foundational value gains — the baseline</div>
                    </div>
                    <div className="legend-value">
                      <div className="legend-amount">£{foundationalValue.toFixed(2)}M</div>
                      <div className="legend-percent">57%</div>
                    </div>
                  </div>

                  <div className="legend-item">
                    <div className="legend-color color-recoverable"></div>
                    <div className="legend-content">
                      <div className="legend-title">Extra value you're gaining</div>
                      <div className="legend-description">Your enhanced value gains — the dividends of building on solid foundational practices</div>
                    </div>
                    <div className="legend-value">
                      <div className="legend-amount">£{stretchValueGained.toFixed(2)}M</div>
                      <div className="legend-percent">3.3%</div>
                    </div>
                  </div>

                  <div className="legend-item">
                    <div className="legend-color color-potential"></div>
                    <div className="legend-content">
                      <div className="legend-title">Your stretch</div>
                      <div className="legend-description">Further value gains available to you</div>
                    </div>
                    <div className="legend-value">
                      <div className="legend-amount">£{stretchValueAvailable.toFixed(2)}M</div>
                      <div className="legend-percent">6.7%</div>
                    </div>
                  </div>

                  <div className="legend-item">
                    <div className="legend-color color-lost"></div>
                    <div className="legend-content">
                      <div className="legend-title">Permanently lost</div>
                      <div className="legend-description">Transformation friction — can't be eliminated</div>
                    </div>
                    <div className="legend-value">
                      <div className="legend-amount">£{permanentlyLost.toFixed(2)}M</div>
                      <div className="legend-percent">33%</div>
                    </div>
                  </div>
                </>
              ) : zone === 'mastery' ? (
                <>
                  {/* Mastery zone - 4 segments */}
                  <div className="legend-item">
                    <div className="legend-color color-captured"></div>
                    <div className="legend-content">
                      <div className="legend-title">Foundational value realised</div>
                      <div className="legend-description">Your foundational value gains — the baseline</div>
                    </div>
                    <div className="legend-value">
                      <div className="legend-amount">£{foundationalValue.toFixed(2)}M</div>
                      <div className="legend-percent">57%</div>
                    </div>
                  </div>

                  <div className="legend-item">
                    <div className="legend-color color-recoverable"></div>
                    <div className="legend-content">
                      <div className="legend-title">Extra value you're gaining</div>
                      <div className="legend-description">Your enhanced value gains — the dividends of being in the top 10% of performers</div>
                    </div>
                    <div className="legend-value">
                      <div className="legend-amount">£{masteryValueGained.toFixed(2)}M</div>
                      <div className="legend-percent">8.2%</div>
                    </div>
                  </div>

                  <div className="legend-item">
                    <div className="legend-color color-potential"></div>
                    <div className="legend-content">
                      <div className="legend-title">Your stretch</div>
                      <div className="legend-description">Further value gains available to you</div>
                    </div>
                    <div className="legend-value">
                      <div className="legend-amount">£{masteryValueAvailable.toFixed(2)}M</div>
                      <div className="legend-percent">1.8%</div>
                    </div>
                  </div>

                  <div className="legend-item">
                    <div className="legend-color color-lost"></div>
                    <div className="legend-content">
                      <div className="legend-title">Permanently lost</div>
                      <div className="legend-description">Transformation friction — can't be eliminated</div>
                    </div>
                    <div className="legend-value">
                      <div className="legend-amount">£{permanentlyLost.toFixed(2)}M</div>
                      <div className="legend-percent">33%</div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Foundational zone - 3 segments */}
                  <div className="legend-item">
                    <div className="legend-color color-captured"></div>
                    <div className="legend-content">
                      <div className="legend-title">Foundational Value</div>
                      <div className="legend-description">Baseline capability every transformation should achieve</div>
                    </div>
                    <div className="legend-value">
                      <div className="legend-amount">£{foundationalValue.toFixed(2)}M</div>
                      <div className="legend-percent">57%</div>
                    </div>
                  </div>

                  <div className="legend-item">
                    <div className="legend-color color-potential"></div>
                    <div className="legend-content">
                      <div className="legend-title">Potential Further Stretch</div>
                      <div className="legend-description">Additional value accessible once baseline is secured</div>
                    </div>
                    <div className="legend-value">
                      <div className="legend-amount">£{potentialStretch.toFixed(2)}M</div>
                      <div className="legend-percent">10%</div>
                    </div>
                  </div>

                  <div className="legend-item">
                    <div className="legend-color color-lost"></div>
                    <div className="legend-content">
                      <div className="legend-title">Permanently Lost</div>
                      <div className="legend-description">Transformation friction — can't be eliminated</div>
                    </div>
                    <div className="legend-value">
                      <div className="legend-amount">£{permanentlyLost.toFixed(2)}M</div>
                      <div className="legend-percent">33%</div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          <p className="source">Source: McKinsey & Company, "Losing from day one", December 2021</p>
        </div>

        {/* Highlight: Zone */}
        <div className="highlight-box">
          <div className="highlight-label">{zoneContent.highlightLabel}</div>
          <div className="highlight-value">{zoneContent.highlightValue}</div>
          <div className="highlight-subtext">{zoneContent.highlightSubtext}</div>
        </div>

        {/* How Do You Capture It? */}
        <div className="card">
          <h2>How Do You Capture It?</h2>

          <div className="challenger-question">
            {zoneContent.challengerQuestion2}
          </div>

          <p style={{ marginBottom: '8px' }}>
            Value capture comes from two intervention channels. Here's the breakdown:
          </p>

          <div className="two-pie-section">
            {/* Left: The breakdown */}
            <div className="pie-panel">
              <div className="pie-panel-title">{zone === 'mastery' ? 'Optimization by Intervention Type' : 'Capture by Intervention Type'}</div>
              <div className="pie-panel-subtitle">Where does the £{zone === 'stretch' ? stretchValueAvailable.toFixed(2) : zone === 'mastery' ? masteryValueAvailable.toFixed(2) : totalPotential.toFixed(2)}M come from?</div>

              <div className="pie-small-container">
                <div className="pie-small pie-chart-intervention">
                  <div className="pie-small-center">
                    <div className="pie-small-label">{zone === 'mastery' ? 'Remaining' : 'Potential'}</div>
                    <div className="pie-small-value">£{zone === 'stretch' ? stretchValueAvailable.toFixed(2) : zone === 'mastery' ? masteryValueAvailable.toFixed(2) : totalPotential.toFixed(2)}M</div>
                  </div>
                </div>
              </div>

              <div className="legend-compact">
                <div className="legend-compact-item">
                  <div className="legend-compact-color color-system"></div>
                  <div className="legend-compact-content">
                    <div className="legend-compact-title">System Interventions</div>
                    <div className="legend-compact-desc">Organisational capability improvements</div>
                  </div>
                  <div className="legend-compact-value">
                    <div className="legend-compact-amount">£{zone === 'stretch' ? (stretchValueAvailable * 0.685).toFixed(2) : zone === 'mastery' ? (masteryValueAvailable * 0.685).toFixed(2) : systemValue.toFixed(2)}M</div>
                    <div className="legend-compact-percent">68.5%</div>
                  </div>
                </div>
                <div className="legend-compact-item">
                  <div className="legend-compact-color color-leadership"></div>
                  <div className="legend-compact-content">
                    <div className="legend-compact-title">Leadership Interventions</div>
                    <div className="legend-compact-desc">Psychological flexibility coaching</div>
                  </div>
                  <div className="legend-compact-value">
                    <div className="legend-compact-amount">£{zone === 'stretch' ? (stretchValueAvailable * 0.315).toFixed(2) : zone === 'mastery' ? (masteryValueAvailable * 0.315).toFixed(2) : leadershipValue.toFixed(2)}M</div>
                    <div className="legend-compact-percent">31.5%</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: What this means */}
            <div className="pie-panel">
              <div className="pie-panel-title">What This Means</div>
              <div className="pie-panel-subtitle">Two levers, one goal: {zone === 'stretch' ? 'capture more value' : zone === 'mastery' ? 'optimization' : 'build baseline capability'}</div>

              <div style={{ padding: '16px 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                  <div style={{ width: '40px', height: '40px', background: '#0EA5E9', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: '700' }}>68%</div>
                  <div>
                    <div style={{ fontWeight: '600', color: '#152C47' }}>System capability</div>
                    <div style={{ fontSize: '13px', color: '#697A90' }}>Fix the machine: dependencies, governance, delivery flow</div>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                  <div style={{ width: '40px', height: '40px', background: '#8B5CF6', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: '700' }}>32%</div>
                  <div>
                    <div style={{ fontWeight: '600', color: '#152C47' }}>Leadership flexibility</div>
                    <div style={{ fontSize: '13px', color: '#697A90' }}>Free the leadership: reduce psychological burden</div>
                  </div>
                </div>

                <div style={{ background: zone === 'stretch' ? '#fef3c7' : zone === 'mastery' ? '#d1fae5' : '#fee2e2', borderRadius: '8px', padding: '16px', marginTop: '16px' }}>
                  <div style={{ fontSize: '13px', color: zone === 'stretch' ? '#92400e' : zone === 'mastery' ? '#065f46' : '#991b1b' }}>
                    <strong>Key insight:</strong> {zone === 'stretch' ? 'Your performance is based on a solid foundation. The remaining potential comes from a stretch towards excellence by adding and embedding enhanced practices to compound this success.' : zone === 'mastery' ? 'Your performance is already well on the way to mastery. The remaining potential comes from fine-tuning and embedding the practices to further compound the success, rather than recovery.' : zoneContent.keyInsight}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Intervention Cards */}
          <div className="intervention-cards">
            <div className="intervention-card intervention-system">
              <div className="intervention-label">System Interventions</div>
              <div className="intervention-title">
                {zone === 'mastery' ? 'UB4 + DF System Optimization' : 'UB4 + DF System Intervention'}
              </div>
              <div className="intervention-value">£{zone === 'stretch' ? (stretchValueAvailable * 0.685).toFixed(2) : zone === 'mastery' ? (masteryValueAvailable * 0.685).toFixed(2) : systemValue.toFixed(2)}M</div>
              <div className="intervention-desc">
                {zone === 'mastery'
                  ? 'The targetted outcome for fine-tuning organizational capability through advanced Uncertainty Management practices'
                  : zone === 'stretch'
                  ? 'The targetted outcome for organisational capability improvement through the Uncertainty Management framework'
                  : 'Build foundational organizational capability through strengthening the 14 dependency factors to reach baseline threshold'
                }
              </div>
              <ul className="intervention-items">
                {zone === 'mastery' ? (
                  <>
                    <li>14 dependency factors already strong</li>
                    <li>Incremental improvements to embed excellence</li>
                    <li>Fine-tuning governance and delivery practices</li>
                  </>
                ) : zone === 'stretch' ? (
                  <>
                    <li>14 dependency factors assessed</li>
                    <li>Hidden work surfaced and addressed</li>
                    <li>Governance and delivery optimised</li>
                  </>
                ) : (
                  <>
                    <li>Strategic clarity and value case quality</li>
                    <li>Delivery flow and interdependency management</li>
                    <li>Hidden work visibility and management</li>
                    <li>Portfolio governance and decision rights</li>
                    <li>Change implementation foundations</li>
                    <li>Stakeholder network health development</li>
                  </>
                )}
              </ul>
            </div>

            <div className="intervention-card intervention-leadership">
              <div className="intervention-label">Leadership Interventions</div>
              <div className="intervention-title">
                {zone === 'mastery' ? 'UB4 + DF Leadership Optimization' : 'UB4 + DF Leadership Intervention'}
              </div>
              <div className="intervention-value">£{zone === 'stretch' ? (stretchValueAvailable * 0.315).toFixed(2) : zone === 'mastery' ? (masteryValueAvailable * 0.315).toFixed(2) : leadershipValue.toFixed(2)}M</div>
              <div className="intervention-desc">
                {zone === 'mastery'
                  ? 'The targetted outcome through advanced ACT-based executive coaching to sustain psychological flexibility at peak performance'
                  : 'The targetted outcome through ACT-based executive coaching to improve psychological flexibility'
                }
              </div>
              <ul className="intervention-items">
                {zone === 'mastery' ? (
                  <>
                    <li>10 anxiety patterns already well-managed</li>
                    <li>Advanced optimization coaching</li>
                    <li>Sustaining peak psychological flexibility</li>
                  </>
                ) : (
                  <>
                    <li>10 anxiety patterns addressed</li>
                    <li>Evidence-based (Skews 2018 RCT)</li>
                    <li>8% effect size on mental wellbeing</li>
                  </>
                )}
              </ul>
            </div>
          </div>
        </div>

        {/* Top 3 Leadership Anxieties */}
        <div className="card">
          <h2>Top 3 Leadership Anxieties Requiring Attention</h2>
          <p style={{ color: '#697A90', marginBottom: '16px' }}>
            These anxieties have the highest scores and greatest impact on leadership psychological flexibility.
          </p>

          <div className="dependency-list">
            {top3Anxieties.map((anx, index) => (
              <div key={anx.id} className={`dependency-item ${getScoreBand(anx.score)}`}>
                <div className="dependency-header">
                  <div>
                    <div className="dependency-title">{index + 1}. {anx.name}</div>
                    <div className="dependency-description">{anx.summary}</div>
                  </div>
                  <div className="dependency-score">{anx.score}/10</div>
                </div>
                {anx.impact && (
                  <div className="dependency-impact">
                    <p><strong>Impact:</strong> {anx.impact}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Recommendations */}
        <div className="card">
          <h2>Recommended Next Steps</h2>
          <p style={{ color: '#697A90', marginBottom: '16px' }}>
            <strong>{results.leaderName || 'Leader'}</strong>, based on your data, here are evidence-based ACT interventions to {zone === 'foundational' ? 'build foundational leadership psychological flexibility' : zone === 'stretch' ? 'capture your stretch potential' : 'sustain leadership mastery'}:
          </p>

          {/* Dynamic recommendations based on top 3 anxieties */}
          {top3Anxieties.map((anx, index) => (
            <div key={anx.id} className="recommendation-item">
              <h4>{index + 1}. {anx.actionSummary || anx.name}</h4>
              <p>{anx.actionStatement || anx.impact || 'No action statement available.'}</p>
            </div>
          ))}

          <div className="recommendation-item">
            <h4>4. Consider System Capability Assessment</h4>
            <p>Leadership psychological flexibility addresses 31.5% of transformation value loss. The remaining 68.5% is attributable to organizational system factors. Consider completing the System Burden Assessment to understand your organizational dependencies and identify opportunities to address the system-addressable portion of value at risk.</p>
          </div>
        </div>

        {/* CTA Section */}
        <div className="cta-section">
          <div className="cta-question">
            {zone === 'stretch' ? (
              <>
                £{stretchValueAvailable.toFixed(2)}M is sitting on the table, there for the taking.<br />
                Are you daring enough to stretch for it?
              </>
            ) : zone === 'mastery' ? (
              <>
                You've almost captured all of the available value.<br />
                Ready to reach for mastery?
              </>
            ) : (
              <>
                Ready to build your leadership psychological flexibility?<br />
                Let's discuss your path to baseline capability
              </>
            )}
          </div>
          <div className="cta-subtext">
            {zone === 'stretch'
              ? 'Explore stretch interventions to maximize your transformation success'
              : zone === 'mastery'
              ? 'Explore mastery practices to maximize your transformation success'
              : 'Book a Truth Session to explore foundational leadership psychological flexibility development'
            }
          </div>
          <p style={{ marginTop: '24px', marginBottom: '20px', opacity: '0.9' }}>
            {zone === 'stretch'
              ? "Book a Truth Session to discuss your results and explore the recommended next steps in detail. We'll help you identify the ACT interventions that will have the greatest impact on your transformation success."
              : zone === 'mastery'
              ? "Book a Truth Session to discuss your results and explore the recommended next steps in detail. We'll help you identify the advanced ACT practices that will have the greatest impact on your transformation success."
              : "Schedule a call to discuss your anxiety patterns and create a plan to build the foundational leadership psychological flexibility that unlocks transformation value. We'll help you understand what reaching baseline threshold means for your leadership capability and your transformation."
            }
          </p>
          <button onClick={() => window.open('https://calendly.com/andrewkidd/truth-session', '_blank')} className="btn-truth-session">
            Book Truth Session
          </button>
        </div>

        {/* Actions */}
        <div className="card">
          <div className="button-group">
            <button className="btn-secondary" onClick={() => window.print()}>Print Results</button>
            <button className="btn-primary" onClick={() => navigate('/request-diagnostic')}>
              Request System Diagnostic
            </button>
          </div>
        </div>

        {/* Footer */}
        <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: '14px', marginTop: '32px' }}>
          <p>Daring Futures • Leadership Burden Diagnostic • v7.0.0 • Step 4 of 4</p>
        </div>
        </div>
      </div>
    </>
  );
}

export default LeadershipBurdenResults;
